import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, Mail, Trash2, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

/*
 * Branded confirm dialog replacing native window.confirm().
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete tag?",
 *     message: "Removes #Kartra from every client. Cannot be undone.",
 *     confirmText: "Delete",
 *     tone: "danger",   // danger | send | info | default
 *   });
 *   if (!ok) return;
 */

const ConfirmCtx = createContext(null);

const TONE = {
  danger: {
    accent: "#DF1283",   // UN-BERRY
    accentSoft: "rgba(223,18,131,0.08)",
    button: "bg-[#DF1283] hover:bg-[#000000]",
    Icon: Trash2,
    iconBg: "bg-[#DF1283]",
    iconFg: "#FFFFFF",
    default_confirm: "Delete",
  },
  send: {
    accent: "#0FFFE2",
    accentSoft: "rgba(15,255,226,0.10)",
    button: "bg-[#4A0A66] hover:bg-[#000000]",
    Icon: Mail,
    iconBg: "bg-[#0FFFE2]",
    iconFg: "#000000",
    default_confirm: "Send",
  },
  warn: {
    accent: "#FC861E",
    accentSoft: "rgba(252,134,30,0.10)",
    button: "bg-[#FC861E] hover:bg-[#000000]",
    Icon: AlertTriangle,
    iconBg: "bg-[#FC861E]",
    iconFg: "#FFFFFF",
    default_confirm: "Continue",
  },
  info: {
    accent: "#4A0A66",
    accentSoft: "rgba(74,10,102,0.08)",
    button: "bg-[#4A0A66] hover:bg-[#000000]",
    Icon: Info,
    iconBg: "bg-[#4A0A66]",
    iconFg: "#FFFFFF",
    default_confirm: "Confirm",
  },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {opts, resolve}
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ opts: opts || {} });
    });
  }, []);

  const finish = (result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setState(null);
  };

  const tone = TONE[state?.opts?.tone] || TONE.info;
  const Icon = tone.Icon;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) finish(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="branded-confirm-dialog">
          <DialogHeader>
            <div className={`w-11 h-11 rounded-full ${tone.iconBg} flex items-center justify-center mb-2`}>
              <Icon size={20} color={tone.iconFg} strokeWidth={2.5} />
            </div>
            <DialogTitle className="text-xl font-display font-black text-[#000000]">
              {state?.opts?.title || "Are you sure?"}
            </DialogTitle>
            {state?.opts?.message && (
              <DialogDescription className="text-sm text-gray-600 leading-relaxed pt-1">
                {state.opts.message}
              </DialogDescription>
            )}
          </DialogHeader>
          {state?.opts?.body /* optional custom JSX passed by caller */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
            <button
              type="button"
              onClick={() => finish(false)}
              data-testid="branded-confirm-cancel"
              className="px-5 py-2.5 rounded-full text-sm font-bold text-[#000000] bg-white border-2 border-gray-200 hover:border-[#000000] hover:bg-gray-50 transition-colors"
            >
              {state?.opts?.cancelText || "Cancel"}
            </button>
            <button
              type="button"
              onClick={() => finish(true)}
              data-testid="branded-confirm-ok"
              className={`px-5 py-2.5 rounded-full text-sm font-bold text-white transition-colors ${tone.button}`}
              autoFocus
            >
              {state?.opts?.confirmText || tone.default_confirm}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
